import { useState } from "react";
import {
    Button,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    IconButton,
} from "@material-tailwind/react";
import { Form, Formik, useField } from "formik";
import * as Yup from "yup";
import FormikPasswordStrength from "components/formik-password-input/formik-password-input";
import FormikInput from "components/form/formik-input";

export default function UpdatePasswordDialog({
    handlePasswordSubmit,
    showUpdatePasswordDialog,
    setShowUpdatePasswordDialog,
}) {
    const handleClose = () => {
        setShowUpdatePasswordDialog((show) => !show);
    };
    // Validation schema
    const validationSchemaPassword = Yup.object().shape({
        password: Yup.string()
            .min(8, "Password must be at least 8 characters")
            // See resetconfirm.js: verbatim passwords (#1097) mean this is the
            // only thing rejecting an all-whitespace credential. This schema is
            // the one that runs; account.js passes a validationSchemaPassword
            // prop this component does not accept.
            .matches(/\S/, "Password cannot be only spaces")
            .required("Required"),
        confirm_password: Yup.string()
            .required("Required")
            .oneOf([Yup.ref("password"), null], "Passwords must match")
            .min(8, "Password must be at least 8 characters"),
    });

    // Confirm password field with show/hide toggle
    const ConfirmPasswordField = ({ name, label }) => {
        const [field, meta] = useField(name);
        const [showPassword, setShowPassword] = useState(false);

        return (
            <div className="mb-4 font-body rounded-xl relative space-y-2">
                <div className="mb-4 font-body rounded-xl relative">
                    <FormikInput
                        {...field}
                        name={name}
                        type={showPassword ? "text" : "password"}
                        label={label}
                        color="gray"
                        autoComplete="new-password"
                        className="pr-10"
                    />
                </div>
                <IconButton
                    variant="text"
                    size="sm"
                    ripple={false}
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="!absolute right-2 top-2 my-auto rounded-full"
                >
                    <span className="material-icons text-gray-600">
                        {showPassword ? "visibility_off" : "visibility"}
                    </span>
                </IconButton>
                {meta.touched && meta.error && (
                    <div className="text-xs text-red-600 mt-1">
                        {meta.error}
                    </div>
                )}
            </div>
        );
    };

    return (
        <Dialog
            open={showUpdatePasswordDialog}
            handler={handleClose}
            className="dialog"
        >
            <DialogHeader className="dialog__header">
                Update Password
            </DialogHeader>
            <Formik
                onSubmit={handlePasswordSubmit}
                validationSchema={validationSchemaPassword}
                initialValues={{
                    password: "",
                    confirm_password: "",
                }}
            >
                {({ isValid, dirty }) => (
                    <Form>
                        <DialogBody className="dialog__body">
                            <div className="mb-4 font-body p-4 card-inner-body rounded-xl">
                                <div className="mb-4">
                                    <FormikPasswordStrength
                                        name="password"
                                        label="New Password"
                                        color="gray"
                                        autoComplete="new-password"
                                        showStrength={true}
                                    />
                                </div>

                                <ConfirmPasswordField
                                    name="confirm_password"
                                    label="Confirm New Password"
                                />
                            </div>
                        </DialogBody>
                        <DialogFooter className="dialog__footer">
                            <div className="bottom-4">
                                <Button
                                    type="button"
                                    className="btn btn--secondary mr-2"
                                    variant="filled"
                                    onClick={handleClose}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={!dirty || !isValid}
                                    onClick={handleClose}
                                    className="btn btn--primary"
                                    variant="filled"
                                >
                                    Save
                                </Button>
                            </div>
                        </DialogFooter>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
}
